import { useState, useEffect } from 'react'
import { Bot, Sparkles, FileText, Settings } from 'lucide-react'

interface AIPromptsData {
  total: number
  active: number
  categories: {
    description: number
    pricing: number
    categorization: number
    other: number
  }
}

export default function AIPromptsCard() {
  const [promptsData, setPromptsData] = useState<AIPromptsData>({
    total: 0,
    active: 0,
    categories: {
      description: 0,
      pricing: 0,
      categorization: 0,
      other: 0
    }
  })
  const [loading, setLoading] = useState(true)
  const [promptName, setPromptName] = useState<string>('')

  useEffect(() => {
    fetchPromptsData()
  }, [])

  const fetchPromptsData = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/ai-prompts-pg')
      const data = await response.json()

      if (data.success && data.data) {
        setPromptName(data.data.name)
        setPromptsData({
          total: 1,
          active: data.data.is_active ? 1 : 0,
          categories: {
            description: 0,
            pricing: 0,
            categorization: 0,
            other: 1
          }
        })
      }
    } catch (error) {
      console.error('Error fetching AI prompts:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1A1F2E, #242938)',
      border: '2px solid #B91BFF',
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 20px rgba(185, 27, 255, 0.2)',
      height: '100%'
    }}>
      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, transparent 50%, rgba(185, 27, 255, 0.2) 50%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'rgba(185, 27, 255, 0.2)',
          border: '2px solid #B91BFF',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Sparkles style={{ width: 24, height: 24, color: '#B91BFF' }} />
        </div>
        <h3 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '20px',
          fontWeight: 700,
          color: '#B91BFF',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          margin: 0,
          textShadow: '0 0 10px rgba(185, 27, 255, 0.6)'
        }}>
          AI Prompts
        </h3>
      </div>

      {/* Total Prompts */}
      <div style={{
        background: 'rgba(185, 27, 255, 0.1)',
        border: '1px solid rgba(185, 27, 255, 0.3)',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Total Prompts
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#B91BFF',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {promptsData.total}
          </div>
        </div>
        <div style={{
          fontSize: '14px',
          color: '#00FF00',
          fontWeight: 700,
          fontFamily: 'Rajdhani, sans-serif'
        }}>
          {promptsData.active} Active
        </div>
      </div>

      {/* Category Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'rgba(0, 240, 255, 0.1)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Description
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#00F0FF',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {promptsData.categories.description}
          </div>
        </div>

        <div style={{
          background: 'rgba(107, 207, 127, 0.1)',
          border: '1px solid rgba(107, 207, 127, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Pricing
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#6BCF7F',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {promptsData.categories.pricing}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 217, 61, 0.1)',
          border: '1px solid rgba(255, 217, 61, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Categorization
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FFD93D',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {promptsData.categories.categorization}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 107, 53, 0.1)',
          border: '1px solid rgba(255, 107, 53, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Other
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FF6B35',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {promptsData.categories.other}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <button style={{
        width: '100%',
        padding: '12px',
        background: 'linear-gradient(135deg, #B91BFF, #9D00FF)',
        border: '2px solid #B91BFF',
        borderRadius: '6px',
        color: '#E0E6F0',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '14px',
        textTransform: 'uppercase',
        fontFamily: 'Rajdhani, sans-serif',
        boxShadow: '0 0 20px rgba(185, 27, 255, 0.4)',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 0 30px rgba(185, 27, 255, 0.6)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 0 20px rgba(185, 27, 255, 0.4)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
      onClick={() => window.location.href = '/admin/ai-prompts'}
      >
        <Settings style={{ width: 16, height: 16 }} />
        Configure Prompts
      </button>
    </div>
  )
}
